import clsx from 'clsx';
import React from 'react';
import Icon from '../../atoms/Icon/Icon';
import type { IconName } from '../../icons/types';

export type AuthProvider = 'google' | 'microsoft';

const ICON_BY_PROVIDER: Record<AuthProvider, IconName> = {
  google: 'google',
  microsoft: 'microsoft',
};

type AuthProviderButtonProps = {
  ariaLabel?: string;
  className?: string;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  isLoading?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  provider: AuthProvider;
  testid?: string;
  text: string;
};

function AuthProviderButtonSpinner(): JSX.Element {
  return (
    <span aria-hidden="true" className="ui-auth-provider-button__spinner">
      <svg className="ui-auth-provider-button__spinner-svg" viewBox="0 0 24 24" focusable="false">
        <circle className="ui-auth-provider-button__spinner-track" cx="12" cy="12" r="9" fill="none" strokeWidth="3" />
        <circle
          className="ui-auth-provider-button__spinner-head"
          cx="12"
          cy="12"
          r="9"
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

const AuthProviderButton = React.forwardRef<HTMLButtonElement, AuthProviderButtonProps>(
  ({ ariaLabel, className, isDisabled = false, isFullWidth = true, isLoading = false, onClick, provider, testid, text }, ref) => {
    const isInteractionBlocked = isDisabled || isLoading;
    const rootClassName = clsx(
      'ui-auth-provider-button',
      `ui-auth-provider-button--${provider}`,
      {
        'ui-auth-provider-button--full-width': isFullWidth,
        'ui-auth-provider-button--loading': isLoading,
      },
      className,
    );
    return (
      <button
        ref={ref}
        aria-busy={isLoading || undefined}
        aria-disabled={isDisabled || undefined}
        aria-label={ariaLabel ?? text}
        className={rootClassName}
        data-testid={testid}
        disabled={isInteractionBlocked}
        onClick={onClick}
        type="button"
      >
        <span className="ui-auth-provider-button__content">
          <span aria-hidden="true" className="ui-auth-provider-button__icon">
            <Icon name={ICON_BY_PROVIDER[provider]} />
          </span>
          <span className="ui-auth-provider-button__label">{text}</span>
        </span>
        {isLoading && <AuthProviderButtonSpinner />}
      </button>
    );
  },
);

AuthProviderButton.displayName = 'AuthProviderButton';

export default AuthProviderButton;
